import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Navbar, MobileNav } from "@/components/Navbar";
import { api, fileUrl } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { LocationSelect } from "@/components/LocationSelect";
import { PostcodeField } from "@/components/PostcodeField";
import { ImageUpload } from "@/components/ImageUpload";
import { Checkbox } from "@/components/ui/checkbox";
import { CompBadge } from "@/components/OpportunityCard";
import { ShareButton } from "@/components/ShareButton";
import { opportunityShareUrl } from "@/lib/share";
import {
  Loader2, ArrowLeft, ArrowRight, Check, MapPin, Users, Calendar, Globe, Target, Zap,
  Plus, Minus, PartyPopper, Compass, Sparkles,
} from "lucide-react";
import { toast } from "sonner";
import { AIButton, AIPanel, CopyButton, useAIAssist } from "@/components/AIAssist";

const OPP_CATEGORIES = ["Food & Drink", "Fashion", "Beauty", "Fitness", "Lifestyle", "Family", "Events", "Travel", "Entertainment", "Other"];
const DELIVERABLE_TYPES = ["Instagram Reel", "Instagram Post", "Instagram Stories", "TikTok Video", "YouTube", "UGC Content", "Attend Event", "Visit Venue", "Product Review", "Other"];
const RADIUS_OPTIONS = [
  { value: "5", label: "Within 5 miles" },
  { value: "10", label: "Within 10 miles" },
  { value: "25", label: "Within 25 miles" },
  { value: "50", label: "Within 50 miles" },
  { value: "anywhere", label: "Anywhere in the UK" },
];

const blank = {
  title: "", category: "", location: "", description: "", cover_image: "",
  postcode: "", county: "", lat: null, lng: null, location_source: "", publish_postcode: false,
  deliverables_list: [], additional_requirements: "",
  compensation_type: "paid", payment_amount: "", gift_description: "",
  niches: [], platforms: [], min_followers: "", radius: "anywhere", creators_needed: 1,
  content_types: [], min_engagement: "",
  deadline: "", campaign_date: "", content_deadline: "",
  visibility: "public",
  attestation: false,
  campaign_id: "",
  opp_type: "collaboration",
  venue_name: "", event_date: "", start_time: "", end_time: "",
  what_creators_receive: "", complimentary: "", plus_one_allowed: false,
  content_expectations: "", enable_waitlist: false,
};
const OPP_TYPES = [
  { v: "collaboration", l: "Collaboration" },
  { v: "paid_campaign", l: "Paid Campaign" },
  { v: "gifted", l: "Gifted" },
  { v: "event", l: "Event / Experience" },
];
const STEPS = ["The opportunity", "Deliverables", "Reward", "Ideal creator", "Dates", "Find creators", "Preview"];

function StepHeader({ title, subtitle }) {
  return (
    <div className="mb-5">
      <h2 className="font-display text-xl font-bold text-zinc-900">{title}</h2>
      {subtitle && <p className="mt-1 text-sm text-zinc-500">{subtitle}</p>}
    </div>
  );
}

function Chip({ active, onClick, children, testid }) {
  return (
    <button type="button" data-testid={testid} onClick={onClick}
      className={`rounded-full border px-3.5 py-1.5 text-sm font-medium transition-colors ${active ? "border-emerald-600 bg-emerald-50 text-emerald-700" : "border-zinc-200 text-zinc-600 hover:bg-zinc-50"}`}>
      {children}
    </button>
  );
}

function VisibilityOption({ value, current, onClick, Icon, title, desc }) {
  const active = current === value;
  return (
    <button type="button" data-testid={`visibility-${value}`} onClick={() => onClick(value)}
      className={`flex w-full items-start gap-3 rounded-2xl border p-4 text-left transition-all ${active ? "border-emerald-500 bg-emerald-50 shadow-sm" : "border-zinc-200 hover:bg-zinc-50"}`}>
      <div className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-xl ${active ? "bg-emerald-600 text-white" : "bg-zinc-100 text-zinc-500"}`}>
        <Icon className="h-5 w-5" />
      </div>
      <div>
        <p className="font-display font-semibold text-zinc-900">{title}</p>
        <p className="text-sm text-zinc-500">{desc}</p>
      </div>
    </button>
  );
}

export default function CreateOpportunity() {
  const navigate = useNavigate();
  const { id } = useParams();
  const editing = !!id;
  const [meta, setMeta] = useState({});
  const [d, setD] = useState(blank);
  const [campaigns, setCampaigns] = useState([]);
  const [step, setStep] = useState(0);
  const [busy, setBusy] = useState(false);
  const [published, setPublished] = useState(null);
  const [loading, setLoading] = useState(editing);
  const [biz, setBiz] = useState({});
  const [aiEnabled, setAiEnabled] = useState(true);
  const [pending, setPending] = useState(null);
  const [descDraft, setDescDraft] = useState("");
  const descAI = useAIAssist("/ai/opportunity/assist");
  const delivAI = useAIAssist("/ai/opportunity/assist");
  const hashAI = useAIAssist("/ai/opportunity/assist");
  const assistAI = useAIAssist("/ai/opportunity/assist");

  useEffect(() => {
    api.get("/ai/status").then((r) => setAiEnabled(!!r.data.enabled)).catch(() => {});
  }, []);

  useEffect(() => {
    api.get("/meta").then((r) => setMeta(r.data));
    api.get("/campaigns").then((r) => setCampaigns(r.data.campaigns || [])).catch(() => {});
    api.get("/business/profile/me").then((r) => {
      setBiz(r.data || {});
      if (!editing && r.data.location) setD((s) => ({
        ...s,
        location: s.location || r.data.location,
        postcode: s.postcode || r.data.postcode || "",
        county: s.county || r.data.county || "",
        lat: s.lat ?? r.data.lat ?? null,
        lng: s.lng ?? r.data.lng ?? null,
        location_source: s.location_source || (r.data.lat != null ? "business" : "dropdown"),
      }));
    }).catch(() => {});
    if (editing) {
      api.get(`/opportunities/${id}`).then((r) => {
        const o = r.data;
        setD({
          ...blank, ...o,
          payment_amount: o.payment_amount ?? "",
          min_followers: o.min_followers ?? "",
          min_engagement: o.min_engagement ?? "",
          content_types: o.content_types || [],
          deliverables_list: o.deliverables_list || [],
          radius: o.radius || "anywhere",
        });
      }).catch(() => toast.error("Could not load opportunity")).finally(() => setLoading(false));
    }
  }, [id, editing]);

  const set = (k, v) => setD((s) => ({ ...s, [k]: v }));
  const toggle = (k, v) => setD((s) => ({ ...s, [k]: s[k].includes(v) ? s[k].filter((x) => x !== v) : [...s[k], v] }));

  const addDeliverable = (type) => setD((s) => {
    if (s.deliverables_list.find((x) => x.type === type)) return s;
    return { ...s, deliverables_list: [...s.deliverables_list, { type, quantity: 1 }] };
  });
  const setQty = (type, delta) => setD((s) => ({
    ...s, deliverables_list: s.deliverables_list.map((x) => x.type === type ? { ...x, quantity: Math.max(1, x.quantity + delta) } : x),
  }));
  const removeDeliverable = (type) => setD((s) => ({ ...s, deliverables_list: s.deliverables_list.filter((x) => x.type !== type) }));

  const aiContext = () => ({
    business_name: biz.business_name || "",
    business_category: biz.category || "",
    title: d.title,
    opp_type: (OPP_TYPES.find((o) => o.v === d.opp_type) || {}).l || d.opp_type,
    location: d.location,
    category: d.category,
    description: d.description,
    compensation: rewardText() !== "—" ? rewardText() : d.compensation_type,
    event: d.opp_type === "event" ? [d.venue_name, d.event_date, d.what_creators_receive, d.complimentary].filter(Boolean).join(", ") : "",
    deliverables: d.deliverables_list.map((x) => `${x.quantity} ${x.type}`).join(" + "),
    creator_requirements: [d.niches.join(", "), d.min_followers ? `min ${d.min_followers} followers` : "", (d.content_types || []).join(", ")].filter(Boolean).join("; "),
    platforms: d.platforms.join(", "),
    niches: d.niches.join(", "),
    extra: d.additional_requirements,
  });

  const addAiDeliverable = (it) => setD((s) => {
    const type = DELIVERABLE_TYPES.includes(it.type) ? it.type : (DELIVERABLE_TYPES.find((t) => t.toLowerCase().includes(String(it.type || "").toLowerCase().split(" ")[0])) || it.type);
    if (s.deliverables_list.find((x) => x.type === type)) return s;
    return { ...s, deliverables_list: [...s.deliverables_list, { type, quantity: Math.max(1, Number(it.quantity) || 1) }] };
  });

  const runAssist = (action) => { setPending(action); assistAI.run({ action, context: aiContext() }).finally(() => setPending(null)); };

  const stepValid = () => {
    if (step === 0) return d.title.trim() && d.location && d.category && (d.opp_type !== "event" || (d.event_date && d.venue_name.trim()));
    if (step === 1) return d.deliverables_list.length > 0;
    if (step === 2) {
      if (d.compensation_type === "paid") return d.payment_amount !== "" && Number(d.payment_amount) >= 0;
      if (d.compensation_type === "gifted") return d.gift_description.trim();
      return d.payment_amount !== "" && d.gift_description.trim();
    }
    return true;
  };

  const save = async (status) => {
    setBusy(true);
    const payload = {
      ...d, status,
      payment_amount: d.payment_amount === "" ? null : Number(d.payment_amount),
      min_followers: d.min_followers === "" ? null : Number(d.min_followers),
      min_engagement: d.min_engagement === "" || d.min_engagement == null ? null : Number(d.min_engagement),
      creators_needed: Number(d.creators_needed) || 1,
      campaign_id: d.campaign_id || null,
      platforms: (meta.platforms && meta.platforms.length) ? (d.platforms || []).filter((p) => meta.platforms.includes(p)) : (d.platforms || []),
    };
    try {
      let opp;
      if (editing) opp = (await api.put(`/opportunities/${id}`, payload)).data;
      else opp = (await api.post("/opportunities", payload)).data;
      if (status === "draft") {
        toast.success("Draft saved");
        navigate("/business/opportunities");
      } else {
        setPublished(opp);
      }
    } catch { toast.error("Could not save opportunity"); }
    finally { setBusy(false); }
  };

  const dlSummary = d.deliverables_list.map((x) => `${x.quantity} ${x.type}`).join(" + ");
  const rewardText = () => {
    const pay = d.compensation_type !== "gifted" && d.payment_amount !== "" ? `£${d.payment_amount}` : "";
    const gift = d.compensation_type !== "paid" && d.gift_description ? d.gift_description : "";
    return [pay, gift].filter(Boolean).join(" + ") || "—";
  };

  if (loading) return <div className="flex h-screen items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-emerald-600" /></div>;

  // ---- Success screen ----
  if (published) {
    const vis = published.visibility;
    return (
      <div className="min-h-screen bg-[#FAF9F6] nf-pb-nav">
        <Navbar />
        <div className="mx-auto flex max-w-lg flex-col items-center px-4 py-16 text-center">
          <div className="flex h-16 w-16 items-center justify-center rounded-full bg-emerald-100 text-emerald-600"><PartyPopper className="h-8 w-8" /></div>
          <h1 data-testid="opp-success-title" className="mt-5 font-display text-3xl font-bold text-zinc-900">{published.opp_type === "event" ? "Your event is live 🎉" : "Your opportunity is live 🎉"}</h1>
          <p className="mt-2 text-zinc-500">
            {vis === "public" && "Creators can now discover and apply to your opportunity."}
            {vis === "direct" && "Your opportunity is private. Invite creators directly to start receiving responses."}
            {vis === "both" && "Your opportunity is live for creators to apply — and you can invite creators directly too."}
          </p>
          <div className="mt-8 flex w-full flex-col gap-3">
            {published.status === "published" && (
              <div data-testid="success-share-card" className="rounded-2xl border border-emerald-200 bg-emerald-50/70 p-5 text-left">
                <p className="font-display font-bold text-zinc-900">Get more applications</p>
                <p className="mt-1 text-sm text-zinc-600">Share your opportunity with creators on your existing social channels to bring more applicants to Nearfluence.</p>
                <div className="mt-3">
                  <ShareButton testid="success-share" url={opportunityShareUrl(published.id)} title={published.title}
                    text={`${published.title} — we're looking for local creators! Apply on Nearfluence.`}
                    recordKind="opportunity" recordId={published.id}
                    label="Share opportunity" variant="default" className="bg-emerald-600 text-white hover:bg-emerald-700" />
                </div>
              </div>
            )}
            {(vis === "direct" || vis === "both") && (
              <Button data-testid="success-discover" onClick={() => navigate(`/discover?invite=${published.id}`)} className="h-12 rounded-full bg-emerald-600 text-base hover:bg-emerald-700">
                <Compass className="mr-2 h-5 w-5" /> Discover & invite creators
              </Button>
            )}
            <Button data-testid="success-manage" variant={vis === "public" ? "default" : "outline"} onClick={() => navigate(`/business/opportunities/${published.id}`)} className={`h-12 rounded-full text-base ${vis === "public" ? "bg-emerald-600 hover:bg-emerald-700" : ""}`}>
              Manage opportunity
            </Button>
            <Button data-testid="success-back" variant="ghost" onClick={() => navigate("/business/opportunities")} className="rounded-full">Back to opportunities</Button>
          </div>
        </div>
        <MobileNav />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FAF9F6] nf-pb-nav">
      <Navbar />
      <div className="mx-auto max-w-2xl px-4 py-6 sm:px-6">
        <button data-testid="wizard-exit" onClick={() => navigate("/business/opportunities")} className="mb-4 inline-flex items-center gap-1 text-sm text-zinc-500 hover:text-zinc-900"><ArrowLeft className="h-4 w-4" /> Cancel</button>

        {/* Progress */}
        <div className="mb-6">
          <div className="mb-2 flex items-center justify-between text-xs font-medium text-zinc-500">
            <span data-testid="wiz-step-label">{STEPS[step]}</span>
            <span>Step {step + 1} of {STEPS.length}</span>
          </div>
          <div className="flex gap-1.5">
            {STEPS.map((_, i) => (
              <div key={i} className={`h-1.5 flex-1 rounded-full transition-colors ${i <= step ? "bg-emerald-600" : "bg-zinc-200"}`} />
            ))}
          </div>
        </div>

        <div className="rounded-3xl border border-zinc-200 bg-white p-5 sm:p-7">
          {step === 0 && (
            <div>
              <StepHeader title="Tell us about the opportunity" subtitle="Keep it simple — just like you'd describe it to a customer." />
              <div className="space-y-4">
                <div>
                  <Label>Opportunity type</Label>
                  <div className="mt-1.5 grid grid-cols-2 gap-2 sm:grid-cols-4">
                    {OPP_TYPES.map((o) => (
                      <button key={o.v} type="button" data-testid={`wiz-type-${o.v}`}
                        onClick={() => { set("opp_type", o.v); if (o.v === "gifted") set("compensation_type", "gifted"); if (o.v === "paid_campaign") set("compensation_type", "paid"); }}
                        className={`rounded-xl border px-2 py-2.5 text-xs font-semibold transition-colors ${d.opp_type === o.v ? "border-emerald-600 bg-emerald-50 text-emerald-700" : "border-zinc-200 text-zinc-600 hover:bg-zinc-50"}`}>{o.l}</button>
                    ))}
                  </div>
                </div>
                <div><Label>{d.opp_type === "event" ? "Event title" : "Opportunity title"}</Label><Input data-testid="wiz-title" value={d.title} onChange={(e) => set("title", e.target.value)} className="mt-1.5 h-11" placeholder={d.opp_type === "event" ? "Summer rooftop launch night" : "Come and try our new brunch menu"} /></div>
                <div><Label>Location</Label><LocationSelect testid="wiz-location" value={d.location} onChange={(v) => setD((s) => ({ ...s, location: v, postcode: "", county: "", lat: null, lng: null, location_source: "dropdown" }))} groups={meta.location_groups || []} placeholder="Where is this happening?" className="mt-1.5 h-11" />
                  <p className="mt-1 text-xs text-zinc-400">Defaults to your business location. Choose a different town, or use a postcode below for a specific branch/venue.</p>
                  <div className="mt-2">
                    <PostcodeField testid="wiz-postcode"
                      value={{ postcode: d.postcode, town: d.location, county: d.county, lat: d.lat, lng: d.lng, source: d.location_source }}
                      onChange={(loc) => setD((s) => ({ ...s, postcode: loc.postcode, location: loc.town, county: loc.county, lat: loc.lat, lng: loc.lng, location_source: loc.source }))} />
                  </div>
                </div>
                <div><Label>Category</Label>
                  <Select value={d.category} onValueChange={(v) => set("category", v)}>
                    <SelectTrigger data-testid="wiz-category" className="mt-1.5 h-11"><SelectValue placeholder="Choose a category" /></SelectTrigger>
                    <SelectContent>{OPP_CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div><Label>{d.opp_type === "event" ? "Event description" : "Campaign brief"}</Label><Textarea data-testid="wiz-desc" rows={4} value={d.description} onChange={(e) => set("description", e.target.value)} className="mt-1.5" placeholder="Tell creators what you're promoting, what you'd like them to experience and what you're looking for." />
                  <div className="mt-2 flex items-center justify-between gap-2">
                    <span className="text-xs text-zinc-400">Rough notes are fine — let AI tidy them up.</span>
                    {aiEnabled ? (
                      <AIButton testid="ai-improve-desc" loading={descAI.loading} label="Improve with AI"
                        onClick={() => descAI.run({ action: "improve_description", context: aiContext(), current_text: d.description }).then((r) => r && setDescDraft(r.text || ""))} />
                    ) : <span className="text-xs text-zinc-400">AI Assist is temporarily unavailable.</span>}
                  </div>
                  {descAI.result && (
                    <AIPanel testid="ai-desc-panel" title="Improved description" loading={descAI.loading}
                      onRegenerate={() => descAI.run({ action: "improve_description", context: aiContext(), current_text: d.description }).then((r) => r && setDescDraft(r.text || ""))}
                      onDismiss={() => descAI.reset()}>
                      <Textarea data-testid="ai-desc-draft" rows={5} value={descDraft} onChange={(e) => setDescDraft(e.target.value)} className="bg-white" />
                      <div className="mt-2">
                        <Button data-testid="ai-desc-use" onClick={() => { set("description", descDraft); descAI.reset(); toast.success("Description updated"); }} className="h-9 rounded-full bg-emerald-600 text-xs hover:bg-emerald-700">Use suggestion</Button>
                      </div>
                    </AIPanel>
                  )}
                </div>

                {d.opp_type === "event" && (
                  <div data-testid="wiz-event-fields" className="space-y-4 rounded-2xl border border-emerald-200 bg-emerald-50/50 p-4">
                    <p className="font-display text-sm font-bold text-emerald-800">Event details</p>
                    <div><Label>Venue name</Label><Input data-testid="wiz-venue" value={d.venue_name} onChange={(e) => set("venue_name", e.target.value)} className="mt-1.5 h-11" placeholder="Sky Bar, The Roof Gardens" /></div>
                    <div className="grid grid-cols-3 gap-3">
                      <div><Label>Event date</Label><Input data-testid="wiz-event-date" type="date" value={d.event_date} onChange={(e) => set("event_date", e.target.value)} className="mt-1.5 h-11" /></div>
                      <div><Label>Start</Label><Input data-testid="wiz-start-time" type="time" value={d.start_time} onChange={(e) => set("start_time", e.target.value)} className="mt-1.5 h-11" /></div>
                      <div><Label>End</Label><Input data-testid="wiz-end-time" type="time" value={d.end_time} onChange={(e) => set("end_time", e.target.value)} className="mt-1.5 h-11" /></div>
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <div><Label>Creator spaces</Label><Input data-testid="wiz-spaces" type="number" min="1" value={d.creators_needed} onChange={(e) => set("creators_needed", e.target.value)} className="mt-1.5 h-11" /></div>
                      <div><Label>What creators receive</Label><Input data-testid="wiz-receive" value={d.what_creators_receive} onChange={(e) => set("what_creators_receive", e.target.value)} className="mt-1.5 h-11" placeholder="Free entry + dinner" /></div>
                    </div>
                    <div><Label>Complimentary food/drink <span className="text-zinc-400">(optional)</span></Label><Input data-testid="wiz-complimentary" value={d.complimentary} onChange={(e) => set("complimentary", e.target.value)} className="mt-1.5 h-11" placeholder="3-course tasting menu + welcome drink" /></div>
                    <div><Label>Content expectations <span className="text-zinc-400">(optional)</span></Label><Textarea data-testid="wiz-content-exp" rows={2} value={d.content_expectations} onChange={(e) => set("content_expectations", e.target.value)} className="mt-1.5" placeholder="1 Reel + 3 Stories on the night" /></div>
                    <div className="flex items-center gap-6">
                      <label className="flex items-center gap-2 text-sm text-zinc-700"><Checkbox data-testid="wiz-plusone" checked={d.plus_one_allowed} onCheckedChange={(v) => set("plus_one_allowed", !!v)} /> Plus-one allowed</label>
                      <label className="flex items-center gap-2 text-sm text-zinc-700"><Checkbox data-testid="wiz-waitlist" checked={d.enable_waitlist} onCheckedChange={(v) => set("enable_waitlist", !!v)} /> Enable waitlist when full</label>
                    </div>
                  </div>
                )}

                <div><Label>Cover image <span className="text-zinc-400">(optional)</span></Label>
                  <p className="mb-2 text-xs text-zinc-400">Makes your {d.opp_type === "event" ? "event" : "opportunity"} stand out on the creator job board.</p>
                  <ImageUpload testid="wiz-cover" value={d.cover_image} onChange={(v) => set("cover_image", v)} label="Add cover" />
                </div>
              </div>
            </div>
          )}

          {step === 1 && (
            <div>
              <StepHeader title="What do you want the creator to do?" subtitle="Pick one or more deliverables and set how many of each." />
              <div className="flex flex-wrap gap-2">
                {DELIVERABLE_TYPES.map((t) => (
                  <Chip key={t} testid={`wiz-deliverable-${t.replace(/\s+/g, "-").toLowerCase()}`} active={!!d.deliverables_list.find((x) => x.type === t)} onClick={() => addDeliverable(t)}>+ {t}</Chip>
                ))}
              </div>
              {aiEnabled && (
                <div className="mt-3">
                  <AIButton testid="ai-suggest-deliverables" loading={delivAI.loading} label="Suggest deliverables with AI"
                    onClick={() => delivAI.run({ action: "deliverables", context: aiContext() })} />
                </div>
              )}
              {delivAI.result?.items && (
                <AIPanel testid="ai-deliv-panel" title="Suggested deliverables" loading={delivAI.loading}
                  onRegenerate={() => delivAI.run({ action: "deliverables", context: aiContext() })}
                  onDismiss={() => delivAI.reset()}>
                  <div className="space-y-2">
                    {delivAI.result.items.map((it, i) => (
                      <div key={i} className="flex items-center gap-2 rounded-xl border border-emerald-200 bg-white px-3 py-2 text-sm">
                        <span className="shrink-0 font-medium text-zinc-800">{it.quantity}× {it.type}</span>
                        {it.note && <span className="truncate text-xs text-zinc-500">{it.note}</span>}
                        <button type="button" data-testid={`ai-deliv-add-${i}`} onClick={() => addAiDeliverable(it)}
                          className="ml-auto shrink-0 text-xs font-semibold text-emerald-700 hover:underline">Add</button>
                      </div>
                    ))}
                  </div>
                </AIPanel>
              )}
              {d.deliverables_list.length > 0 && (
                <div className="mt-5 space-y-2" data-testid="wiz-deliverables-selected">
                  {d.deliverables_list.map((x) => (
                    <div key={x.type} className="flex items-center gap-3 rounded-xl border border-zinc-200 bg-zinc-50 px-3 py-2">
                      <span className="flex-1 text-sm font-medium text-zinc-800">{x.type}</span>
                      <div className="flex items-center gap-2">
                        <button type="button" data-testid={`qty-minus-${x.type.replace(/\s+/g, "-").toLowerCase()}`} onClick={() => setQty(x.type, -1)} className="flex h-7 w-7 items-center justify-center rounded-full border border-zinc-300 text-zinc-600 hover:bg-white"><Minus className="h-3.5 w-3.5" /></button>
                        <span className="w-6 text-center font-mono font-semibold">{x.quantity}</span>
                        <button type="button" data-testid={`qty-plus-${x.type.replace(/\s+/g, "-").toLowerCase()}`} onClick={() => setQty(x.type, 1)} className="flex h-7 w-7 items-center justify-center rounded-full border border-zinc-300 text-zinc-600 hover:bg-white"><Plus className="h-3.5 w-3.5" /></button>
                      </div>
                      <button type="button" onClick={() => removeDeliverable(x.type)} className="text-xs text-rose-500 hover:underline">Remove</button>
                    </div>
                  ))}
                </div>
              )}
              <div className="mt-5"><Label>Additional instructions <span className="text-zinc-400">(optional)</span></Label><Textarea data-testid="wiz-additional" rows={3} value={d.additional_requirements} onChange={(e) => set("additional_requirements", e.target.value)} className="mt-1.5" placeholder="Any tags to include, hashtags, dos and don'ts…" />
                {aiEnabled && (
                  <div className="mt-2 flex justify-end">
                    <AIButton testid="ai-suggest-hashtags" loading={hashAI.loading} label="Suggest hashtags"
                      onClick={() => hashAI.run({ action: "hashtags", context: aiContext() })} />
                  </div>
                )}
                {hashAI.result?.items && (
                  <AIPanel testid="ai-hash-panel" title="Suggested hashtags" loading={hashAI.loading}
                    onRegenerate={() => hashAI.run({ action: "hashtags", context: aiContext() })}
                    onDismiss={() => hashAI.reset()}>
                    <div className="flex flex-wrap gap-1.5">
                      {hashAI.result.items.map((h, i) => (
                        <span key={i} className="rounded-full border border-emerald-200 bg-white px-2.5 py-1 text-xs text-emerald-700">{h}</span>
                      ))}
                    </div>
                    <div className="mt-3 flex gap-2">
                      <Button data-testid="ai-hash-use" onClick={() => { const tags = hashAI.result.items.join(" "); set("additional_requirements", d.additional_requirements ? `${d.additional_requirements}\n\nHashtags: ${tags}` : `Hashtags: ${tags}`); hashAI.reset(); toast.success("Hashtags added to instructions"); }} className="h-9 rounded-full bg-emerald-600 text-xs hover:bg-emerald-700">Add to instructions</Button>
                      <CopyButton testid="ai-hash-copy" text={hashAI.result.items.join(" ")} label="Copy all" />
                    </div>
                  </AIPanel>
                )}
              </div>
            </div>
          )}

          {step === 2 && (
            <div>
              <StepHeader title="What are you offering creators?" subtitle="You don't have to pay cash — a genuine gift works great for local collaborations." />
              <div className="grid grid-cols-3 gap-2">
                {[{ v: "paid", l: "Paid" }, { v: "gifted", l: "Gifted" }, { v: "paid_gifted", l: "Paid + Gifted" }].map((o) => (
                  <button key={o.v} type="button" data-testid={`wiz-comp-${o.v}`} onClick={() => set("compensation_type", o.v)}
                    className={`rounded-xl border px-3 py-3 text-sm font-semibold transition-colors ${d.compensation_type === o.v ? "border-emerald-600 bg-emerald-50 text-emerald-700" : "border-zinc-200 text-zinc-600 hover:bg-zinc-50"}`}>{o.l}</button>
                ))}
              </div>
              <div className="mt-5 space-y-4">
                {d.compensation_type !== "gifted" && (
                  <div><Label>Payment amount (£)</Label><Input data-testid="wiz-payment" type="number" min="0" value={d.payment_amount} onChange={(e) => set("payment_amount", e.target.value)} className="mt-1.5 h-11" placeholder="75" /></div>
                )}
                {d.compensation_type !== "paid" && (
                  <div><Label>What do they receive?</Label><Input data-testid="wiz-gift" value={d.gift_description} onChange={(e) => set("gift_description", e.target.value)} className="mt-1.5 h-11" placeholder="Complimentary meal for two worth up to £80" /></div>
                )}
              </div>
            </div>
          )}

          {step === 3 && (
            <div>
              <StepHeader title="Who are you looking for?" subtitle="Local relevance and engagement often matter more than follower count." />
              <div className="space-y-5">
                <div><Label>Creator niche</Label>
                  <div className="mt-1.5 flex flex-wrap gap-2">{meta.niches?.map((n) => <Chip key={n} testid={`wiz-niche-${n.toLowerCase()}`} active={d.niches.includes(n)} onClick={() => toggle("niches", n)}>{n}</Chip>)}</div>
                </div>
                <div><Label>Platforms</Label>
                  <div className="mt-1.5 flex gap-2">{(meta.platforms || []).map((p) => <Chip key={p} testid={`wiz-platform-${p}`} active={d.platforms.includes(p)} onClick={() => toggle("platforms", p)}><span className="capitalize">{p}</span></Chip>)}</div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div><Label>Min followers <span className="text-zinc-400">(optional)</span></Label><Input data-testid="wiz-minfoll" type="number" value={d.min_followers} onChange={(e) => set("min_followers", e.target.value)} className="mt-1.5 h-11" placeholder="Any" /></div>
                  <div><Label>Creators wanted</Label><Input data-testid="wiz-creators" type="number" min="1" value={d.creators_needed} onChange={(e) => set("creators_needed", e.target.value)} className="mt-1.5 h-11" /></div>
                </div>
                <div><Label>Preferred creator radius</Label>
                  <Select value={d.radius} onValueChange={(v) => set("radius", v)}>
                    <SelectTrigger data-testid="wiz-radius" className="mt-1.5 h-11"><SelectValue /></SelectTrigger>
                    <SelectContent>{RADIUS_OPTIONS.map((r) => <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>)}</SelectContent>
                  </Select>
                </div>

                <details data-testid="wiz-match-advanced" className="rounded-2xl border border-zinc-200 bg-zinc-50/60 p-4">
                  <summary className="cursor-pointer font-display font-semibold text-zinc-800">Help us find your best matches <span className="font-normal text-zinc-400">(optional)</span></summary>
                  <p className="mt-2 text-xs text-zinc-500">These preferences only fine-tune your match recommendations — creators of all sizes can still apply.</p>
                  <div className="mt-3 space-y-4">
                    <div><Label>Content types wanted</Label>
                      <div className="mt-1.5 flex flex-wrap gap-2">
                        {["Instagram Reel", "TikTok", "Story", "Static post", "UGC", "Product review", "Event attendance", "Photography/video"].map((ct) => (
                          <Chip key={ct} testid={`wiz-content-${ct.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`} active={(d.content_types || []).includes(ct)} onClick={() => toggle("content_types", ct)}>{ct}</Chip>
                        ))}
                      </div>
                    </div>
                    <div><Label>Preferred engagement rate % <span className="text-zinc-400">(optional)</span></Label>
                      <Input data-testid="wiz-min-engagement" type="number" step="0.1" min="0" value={d.min_engagement ?? ""} onChange={(e) => set("min_engagement", e.target.value)} className="mt-1.5 h-11" placeholder="Any" />
                    </div>
                  </div>
                </details>
              </div>
            </div>
          )}

          {step === 4 && (
            <div>
              <StepHeader title="Dates" subtitle="Only the application deadline is required — the rest can stay flexible." />
              <div className="space-y-4">
                <div><Label>Application deadline</Label><Input data-testid="wiz-deadline" type="date" value={d.deadline} onChange={(e) => set("deadline", e.target.value)} className="mt-1.5 h-11" /></div>
                <div><Label>Campaign / visit date <span className="text-zinc-400">(optional)</span></Label><Input data-testid="wiz-campdate" type="date" value={d.campaign_date} onChange={(e) => set("campaign_date", e.target.value)} className="mt-1.5 h-11" /></div>
                <div><Label>Content completion deadline <span className="text-zinc-400">(optional)</span></Label><Input data-testid="wiz-contentdeadline" type="date" value={d.content_deadline} onChange={(e) => set("content_deadline", e.target.value)} className="mt-1.5 h-11" /></div>
                <div>
                  <Label>Track under a campaign <span className="text-zinc-400">(optional)</span></Label>
                  <Select value={d.campaign_id || "none"} onValueChange={(v) => set("campaign_id", v === "none" ? "" : v)}>
                    <SelectTrigger data-testid="wiz-campaign" className="mt-1.5 h-11"><SelectValue placeholder="No campaign" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">No campaign</SelectItem>
                      {campaigns.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                  <p className="mt-1.5 text-xs text-zinc-400">Group opportunities into a campaign to measure their combined impact later. Manage campaigns from the Campaigns page.</p>
                </div>
              </div>
            </div>
          )}

          {step === 5 && (
            <div>
              <StepHeader title="How would you like to find creators?" subtitle="You can always change this later." />
              <div className="space-y-3">
                <VisibilityOption value="public" current={d.visibility} onClick={(v) => set("visibility", v)} Icon={Globe} title="🌍 Post publicly" desc="Let creators discover your opportunity and apply." />
                <VisibilityOption value="direct" current={d.visibility} onClick={(v) => set("visibility", v)} Icon={Target} title="🎯 Choose creators myself" desc="Keep it private and invite creators through Discover." />
                <VisibilityOption value="both" current={d.visibility} onClick={(v) => set("visibility", v)} Icon={Zap} title="⚡ Both" desc="Post publicly and invite creators you think are a great fit." />
              </div>
            </div>
          )}

          {step === 6 && (
            <div>
              <StepHeader title="Preview" subtitle="This is how your opportunity will appear to creators." />
              <div data-testid="wiz-preview-card" className="overflow-hidden rounded-2xl border border-zinc-200 bg-white shadow-sm">
                {d.cover_image && <div className="h-36 w-full overflow-hidden bg-zinc-100"><img src={fileUrl(d.cover_image)} alt="" className="h-full w-full object-cover" /></div>}
                <div className="p-5">
                  <div className="flex items-center gap-2">
                    <p className="text-sm font-medium text-zinc-500">{d.category || "—"}</p>
                    <CompBadge type={d.compensation_type} />
                  </div>
                  <h3 className="mt-1 font-display text-xl font-bold text-zinc-900">{d.title || "Your opportunity title"}</h3>
                  <p className="mt-1 flex items-center gap-1 text-sm text-zinc-500"><MapPin className="h-3.5 w-3.5" /> {d.location || "Location"}</p>
                  {d.description && <p className="mt-3 whitespace-pre-line text-sm text-zinc-600">{d.description}</p>}
                  <div className="mt-3 space-y-1.5 text-sm text-zinc-700">
                    {dlSummary && <p>🎥 {dlSummary}</p>}
                    <p className="font-semibold text-emerald-700">{rewardText()}</p>
                    <div className="flex flex-wrap items-center gap-4 text-xs text-zinc-500">
                      <span className="flex items-center gap-1"><Users className="h-3.5 w-3.5" /> {d.creators_needed} needed</span>
                      {d.deadline && <span className="flex items-center gap-1"><Calendar className="h-3.5 w-3.5" /> Apply by {d.deadline}</span>}
                    </div>
                  </div>
                </div>
              </div>
              <div className="mt-3 rounded-xl bg-zinc-50 px-3 py-2 text-xs text-zinc-500">
                Visibility: <span className="font-semibold capitalize text-zinc-700">{d.visibility === "both" ? "Public + direct invites" : d.visibility === "direct" ? "Private (invite only)" : "Public"}</span>
              </div>
              {aiEnabled && (
                <div className="mt-4 rounded-2xl border border-zinc-200 bg-white p-4" data-testid="ai-assist-promote">
                  <div className="flex items-center gap-2"><Sparkles className="h-4 w-4 text-emerald-600" /><span className="font-display text-sm font-bold text-zinc-900">AI Assist — promote your opportunity</span></div>
                  <p className="mt-1 text-xs text-zinc-500">Optional ideas to help you attract the right local creators. Nothing is posted automatically.</p>
                  <div className="mt-3 flex flex-wrap gap-2">
                    <AIButton testid="ai-content-ideas" loading={pending === "content_ideas"} label="Content ideas" onClick={() => runAssist("content_ideas")} />
                    <AIButton testid="ai-talking-points" loading={pending === "talking_points"} label="Talking points" onClick={() => runAssist("talking_points")} />
                    <AIButton testid="ai-caption" loading={pending === "caption"} label="Suggested caption" onClick={() => runAssist("caption")} />
                    <AIButton testid="ai-hashtags-preview" loading={pending === "hashtags"} label="Hashtags" onClick={() => runAssist("hashtags")} />
                  </div>
                  {assistAI.result && (
                    <AIPanel testid="ai-assist-panel" title="AI suggestions" loading={!!pending}
                      onRegenerate={() => runAssist(assistAI.action)} onDismiss={() => assistAI.reset()}>
                      {assistAI.action === "caption" && assistAI.result.text && (
                        <div>
                          <p className="whitespace-pre-line rounded-xl border border-emerald-200 bg-white p-3 text-sm text-zinc-700">{assistAI.result.text}</p>
                          <div className="mt-2"><CopyButton testid="ai-caption-copy" text={assistAI.result.text} /></div>
                        </div>
                      )}
                      {assistAI.action === "content_ideas" && assistAI.result.items && (
                        <div className="space-y-2">
                          {assistAI.result.items.map((it, i) => (
                            <div key={i} className="rounded-xl border border-emerald-200 bg-white p-3">
                              <p className="text-sm font-semibold text-zinc-800">{it.title}</p>
                              {it.description && <p className="text-xs text-zinc-500">{it.description}</p>}
                            </div>
                          ))}
                        </div>
                      )}
                      {assistAI.action === "talking_points" && assistAI.result.items && (
                        <ul className="space-y-1.5">
                          {assistAI.result.items.map((t, i) => (
                            <li key={i} className="flex gap-2 text-sm text-zinc-700"><Check className="mt-0.5 h-3.5 w-3.5 shrink-0 text-emerald-600" /> {t}</li>
                          ))}
                        </ul>
                      )}
                      {assistAI.action === "hashtags" && assistAI.result.items && (
                        <div>
                          <div className="flex flex-wrap gap-1.5">
                            {assistAI.result.items.map((h, i) => (
                              <span key={i} className="rounded-full border border-emerald-200 bg-white px-2.5 py-1 text-xs text-emerald-700">{h}</span>
                            ))}
                          </div>
                          <div className="mt-2"><CopyButton testid="ai-preview-hash-copy" text={assistAI.result.items.join(" ")} label="Copy all" /></div>
                        </div>
                      )}
                    </AIPanel>
                  )}
                </div>
              )}
              <label className="mt-4 flex cursor-pointer items-start gap-2.5 rounded-xl border border-zinc-200 p-3">
                <Checkbox data-testid="wiz-attestation" checked={d.attestation} onCheckedChange={(v) => set("attestation", !!v)} className="mt-0.5" />
                <span className="text-sm text-zinc-600">I confirm that this opportunity is genuine, that I am authorised to offer it and that the compensation and requirements described are accurate.</span>
              </label>
            </div>
          )}
        </div>

        {/* Footer nav */}
        <div className="mt-5 flex items-center gap-2">
          {step > 0 && <Button variant="outline" data-testid="wiz-back" onClick={() => setStep((s) => s - 1)} className="rounded-full"><ArrowLeft className="mr-1 h-4 w-4" /> Back</Button>}
          <div className="flex-1" />
          <Button variant="ghost" data-testid="wiz-save-draft" disabled={busy || !d.title.trim()} onClick={() => save("draft")} className="rounded-full">Save draft</Button>
          {step < STEPS.length - 1 ? (
            <Button data-testid="wiz-next" disabled={!stepValid()} onClick={() => setStep((s) => s + 1)} className="rounded-full bg-emerald-600 hover:bg-emerald-700">Next <ArrowRight className="ml-1 h-4 w-4" /></Button>
          ) : (
            <Button data-testid="wiz-publish" disabled={busy || !d.attestation} onClick={() => save("published")} className="rounded-full bg-emerald-600 hover:bg-emerald-700">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Check className="mr-1 h-4 w-4" /> Publish opportunity</>}</Button>
          )}
        </div>
      </div>
      <MobileNav />
    </div>
  );
}
